import { parseDealOptions } from './parseDealOptions';
import { parseInclusions } from './parseInclusions';
import {
  parseBlackouts,
  parseOverrides,
  parsePropertySetup,
  parseSurchargeGrid,
  parseSurchargePeriods,
} from './parsePricingSheet';
import type { ImportIssue, ParsedPropertySetup, ParsedWorkbook, SheetRows, WorkbookLayout } from './types';

export const V4_PRICING_SHEET = 'Prices and Forecasting';
export const V5_PRICING_SHEET = 'Prices-Forecasting - option 1';
// v5 names it per option, but a v5 file with options 2 and 3 deleted keeps the v4 name.
export const DEAL_OPTIONS_SHEETS = ['Deal Options Import - option 1', 'Deal Options Import'];
export const INCLUSIONS_SHEET = 'Inclusions Import';

const EMPTY_PROPERTY_SETUP: ParsedPropertySetup = {
  country: null,
  travelFrom: null,
  travelTo: null,
  currency: null,
  fxRate: null,
  fxNote: null,
};

function detectLayout(sheetNames: ReadonlyArray<string>): WorkbookLayout | null {
  if (sheetNames.includes(V5_PRICING_SHEET)) {
    return { template: 'v5', primarySheet: V5_PRICING_SHEET };
  }
  if (sheetNames.includes(V4_PRICING_SHEET)) {
    return { template: 'v4', primarySheet: V4_PRICING_SHEET };
  }
  return null;
}

export function parseWorkbook(sheets: Readonly<Record<string, SheetRows>>): ParsedWorkbook {
  const issues: Array<ImportIssue> = [];
  const layout = detectLayout(Object.keys(sheets));

  if (!layout) {
    issues.push({
      section: 'layout',
      severity: 'error',
      message: `This doesn't look like a Pricing Tool workbook: it has no "${V5_PRICING_SHEET}" or "${V4_PRICING_SHEET}" sheet.`,
    });
    return {
      layout: null,
      propertySetup: EMPTY_PROPERTY_SETUP,
      blackouts: [],
      surchargePeriods: [],
      surchargeGrid: null,
      dealOptions: [],
      skippedVariants: [],
      inclusions: [],
      overrides: null,
      issues,
    };
  }

  const pricing = { rows: sheets[layout.primarySheet], sheet: layout.primarySheet, issues };
  const dealOptionsSheet = DEAL_OPTIONS_SHEETS.find((sheet) => sheets[sheet]);
  if (!dealOptionsSheet) {
    issues.push({
      section: 'dealOptions',
      severity: 'error',
      message: 'The workbook has no "Deal Options Import" sheet, so no deal options will load.',
    });
  }
  const { dealOptions, skippedVariants } = dealOptionsSheet
    ? parseDealOptions({ rows: sheets[dealOptionsSheet], sheet: dealOptionsSheet, issues })
    : { dealOptions: [], skippedVariants: [] };
  if (!sheets[INCLUSIONS_SHEET]) {
    issues.push({
      section: 'inclusions',
      severity: 'error',
      message: `The workbook has no "${INCLUSIONS_SHEET}" sheet, so no inclusions will load.`,
    });
  }
  const inclusions = sheets[INCLUSIONS_SHEET]
    ? parseInclusions({ rows: sheets[INCLUSIONS_SHEET], sheet: INCLUSIONS_SHEET, issues })
    : [];

  return {
    layout,
    propertySetup: parsePropertySetup(pricing),
    blackouts: parseBlackouts(pricing),
    surchargePeriods: parseSurchargePeriods(pricing),
    surchargeGrid: parseSurchargeGrid(pricing),
    dealOptions,
    skippedVariants,
    inclusions,
    overrides: parseOverrides(pricing),
    issues,
  };
}
